import React, { useEffect, useMemo, useState } from "react";
import { Typography, Box, CircularProgress, Button } from "@mui/material";
import axios from "axios";
import to from "await-to-js";
import { useNavigate } from "react-router-dom";
import {
  DataGridPro,
  GridColDef,
  GridSortModel,
  GridFilterModel,
  getGridStringOperators,
  GridValueGetter,
  GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { axiosWithAuth } from "../plugins/axios";

const MasterSolutions = () => {
  const [masterSolutions, setMasterSolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>();
  const apiUrl = process.env.REACT_APP_PIPELINE_API;
  const navigate = useNavigate();

  const fetchMasterSolutions = async () => {
    const filters =
      filterModel?.items.map((item) => ({
        column: item.field,
        value: item.value,
        operator: item.operator,
      })) || [];
    const [err, response] = await to(
      axiosWithAuth(
        `${apiUrl}cohortconfiguration/cohorts`,
        {
          params: {
            page: page + 1,
            limit: rowsPerPage,
            masterSolutions: true,
            sortColumn: sortModel[0]?.field || null,
            sortDirection: sortModel[0]?.sort || null,
            // filterColumn: filterModel?.items[0]?.field || null,
            // filterValue: filterModel?.items[0]?.value || null,
            // filterOperator: filterModel?.items[0]?.operator || null,
            filters: JSON.stringify(filters),
          },
        },
        "get"
      )
    );

    if (err) {
      setError("Failed to load master solutions");
    } else {
      const { data, totalCount } = response.data;
      setMasterSolutions(data);
      setTotalCount(totalCount);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchMasterSolutions();
  }, [page, rowsPerPage, sortModel, filterModel]);

  if (loading) {
    return (
      <Box mt={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const columns: GridColDef[] = [
    { field: "programName", headerName: "Program Name", width: 400 },
    {
      field: "quizId",
      headerName: "Quiz ID",
      width: 110,
      renderCell: (params: GridRenderCellParams) => (
        <Button
          color="primary"
          onClick={() =>
            navigate(`/masterSolution/${params.row._id}/list-of-cohorts`)
          }
        >
          {params.value}
        </Button>
      ),
    },
    { field: "quizName", headerName: "quiz name", width: 300 },

    { field: "markedBy", headerName: "Marked By", width: 300 },
    {
      field: "markedOn",
      headerName: "Marked On",
      width: 200,
      renderCell: (params: GridRenderCellParams) =>
        params.row?.markedOn
          ? new Date(params.row?.markedOn).toLocaleString()
          : "-",
      valueGetter: (params: GridValueGetter) => {
        return new Date(params as unknown as string).toLocaleString();
      },
      filterable: false,
      filterOperators: getGridStringOperators().filter(
        (operator) =>
          operator.value === "contains" || operator.value === "equals"
      ),
    },
  ];

  return (
    <Box mt={4}>
      <Typography variant="h4" gutterBottom>
        List of Master Solutions
      </Typography>
      <DataGridPro
        rows={masterSolutions}
        columns={columns}
        paginationMode="server"
        rowCount={totalCount}
        paginationModel={{ page: page, pageSize: rowsPerPage }}
        onPaginationModelChange={(model) => {
          setPage(model.page);
          setRowsPerPage(model.pageSize);
        }}
        pageSizeOptions={[5, 10, 20, 50]}
        disableRowSelectionOnClick
        loading={loading}
        getRowId={(row) => row.quizId}
        onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
        onFilterModelChange={(filter) => setFilterModel(filter)}
      />
    </Box>
  );
};

export default MasterSolutions;
